import React, { useState, useEffect } from 'react';
import {  FaPlus, FaTimes } from 'react-icons/fa';
import './css_pages/Vendors.css';
import {
  getAllVendors,
  createVendor,
  updateVendor,
  deleteVendor
} from '../services/vendorsService';

function Vendors() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vendorName, setVendorName] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [advancedSearchContact, setAdvancedSearchContact] = useState('');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar proveedores al montar el componente
  useEffect(() => {
    fetchVendors();
  }, []);

  // Obtener todos los proveedores
  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllVendors();
      if (response.success) {
        setVendors(response.data);
      }
    } catch (error) {
      setError('Error loading suppliers');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar proveedores según búsqueda
  const filteredVendors = vendors.filter(vendor => {
    const matchesName = vendor.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesContact = advancedSearchContact === '' || 
      vendor.contactName.toLowerCase().includes(advancedSearchContact.toLowerCase());
    return matchesName && matchesContact;
  });

  // Formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Abrir modal para crear
  const handleOpenModal = () => {
    setShowModal(true);
    setVendorName('');
    setContactName('');
    setPhone('');
    setEmail('');
    setEditingId(null);
  };

  // Abrir modal para editar
  const handleEditModal = (vendor) => {
    setShowModal(true);
    setVendorName(vendor.name);
    setContactName(vendor.contactName);
    setPhone(vendor.phone);
    setEmail(vendor.email);
    setEditingId(vendor.id);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setVendorName('');
    setContactName('');
    setPhone('');
    setEmail('');
    setEditingId(null);
  };

  // Guardar o actualizar proveedor
  const handleSaveVendor = async () => {
    const normalizedName = vendorName.trim();
    const normalizedContactName = contactName.trim();
    const normalizedPhone = phone.trim();
    const normalizedEmail = email.trim();
    
    if (normalizedName === '') {
      alert('Please enter the supplier name');
      return;
    }

    // Validación básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      
      if (editingId) {
        const response = await updateVendor(editingId, {name: normalizedName, contactName: normalizedContactName, phone: normalizedPhone, email: normalizedEmail});
        if (response.success) {
          alert('Supplier successfully updated');
          await fetchVendors();
          handleCloseModal();
        }
      } else {
        const response = await createVendor({name: normalizedName, contactName: normalizedContactName, phone: normalizedPhone, email: normalizedEmail});
        if (response.success) {
          alert('Supplier successfully created');
          await fetchVendors();
          handleCloseModal();
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        alert('There is already a supplier with that name.');
      } else if (error.response && error.response.status === 400) {
        alert(error.response.data.message || 'Validation error');
      } else {
        alert('Error saving the provider');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de confirmación de eliminación
  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Cerrar modal de eliminación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteVendor(deleteId);
      if (response.success) {
        alert('Supplier successfully removed');
        await fetchVendors();
      }
      handleCloseDeleteModal();
    } catch (error) {
      alert('Error deleting the provider');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="vendors-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>Vendors</h1>
        </div>
        <button className="btn-primary" onClick={handleOpenModal} disabled={loading}>
          <FaPlus /> Create new vendor
        </button>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchVendors}>Retry</button>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table - Desktop */}
      <div className="table-container desktop-only">
        {loading && <div className="loading">Loading...</div>}
        
        <table className="data-table">
          <thead>
            <tr>
              <th>Supplier Name</th>
              <th>Contact Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVendors.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                  {loading ? 'Loading...' : 'No results found'}
                </td>
              </tr>
            ) : (
              filteredVendors.map((vendor) => (
                <tr key={vendor.id}>
                  <td>{vendor.name}</td>
                  <td>{vendor.contactName}</td>
                  <td>{vendor.phone}</td>
                  <td>{vendor.email}</td>
                  <td>
                    <button 
                      className="btn-action" 
                      onClick={() => handleEditModal(vendor)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-action btn-delete" 
                      onClick={() => handleOpenDeleteModal(vendor.id)}
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cards - Mobile */}
      <div className="vendors-cards-container mobile-only">
        {loading && <div className="loading">Loading...</div>}
        
        {filteredVendors.length === 0 ? (
          <div className="no-results">
            {loading ? 'Loading...' : 'No results found'}
          </div>
        ) : (
          filteredVendors.map((vendor) => (
            <div key={vendor.id} className="vendor-card">
              <div className="vendor-card-header">
                <h3 className="vendor-card-name">{vendor.name}</h3>
              </div>

              <div className="vendor-card-body">
                <div className="vendor-card-row">
                  <div className="vendor-card-item">
                    <span className="vendor-card-label">Contact</span>
                    <span className="vendor-card-value">{vendor.contactName}</span>
                  </div>
                </div>

                <div className="vendor-card-row">
                  <div className="vendor-card-item">
                    <span className="vendor-card-label">Phone</span>
                    <span className="vendor-card-value">{vendor.phone}</span>
                  </div>
                </div>

                <div className="vendor-card-row">
                  <div className="vendor-card-item">
                    <span className="vendor-card-label">Email</span>
                    <span className="vendor-card-value">{vendor.email}</span>
                  </div>
                </div>
              </div>

              <div className="vendor-card-footer">
                <button 
                  className="btn-card-action btn-edit" 
                  onClick={() => handleEditModal(vendor)}
                  disabled={loading}
                >
                  Edit
                </button>
                <button 
                  className="btn-card-action btn-delete-card" 
                  onClick={() => handleOpenDeleteModal(vendor.id)}
                  disabled={loading}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit vendor' : 'Create new vendor'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Supplier Name *</label>
                <input
                  type="text"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder="Enter the supplier's name"
                  autoFocus
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Enter contact name"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveVendor} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminación */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm deletion</h2>
              <button className="btn-close" onClick={handleCloseDeleteModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to remove this provider?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleConfirmDelete} disabled={loading}>
                {loading ? 'Removing...' : 'Eliminate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Vendors;